/**
 * Canonical constants — the single source in code for Design Bible §3/§4/§7/§8/§11.
 * Any number that appears in more than one spec is defined in the Bible and mirrored here once.
 * Do not copy these elsewhere; import them.
 */

// ── §4 Simulation model ────────────────────────────────────────────────────
export const FIXED_DT = 1 / 120; // s. Fixed timestep, 120 Hz.
export const MAX_SUBSTEPS = 8; // Spiral-of-death guard.
export const MAX_FRAME_DELTA = 0.25; // s. Upper bound on accumulator intake (FS-02 §5).

// ── §3 Coordinate system, units & frames ───────────────────────────────────
export const SPAWN_Z = -240; // Hazards are instantiated here.
export const CULL_Z = 8; // Hazards past this return to the pool.
export const HORIZON_Z = -400; // Background plane.
export const TUNNEL_RADIUS = 12; // u. Cylinder about the Z axis.
export const PLAY_RADIUS = 10.5; // u. Playfield disc; the ship is clamped to it.

export const CAMERA_Z = 14;
export const CAMERA_Y = 1.2;
export const CAMERA_FOV = 68; // Vertical FOV, degrees.

// §3 Speed tiers — scroll speed (u/s), unlocked at an intensity threshold.
export interface SpeedTier {
  readonly tier: number;
  readonly speed: number;
  readonly minIntensity: number;
}
export const SPEED_TIERS: readonly SpeedTier[] = [
  { tier: 1, speed: 60, minIntensity: 0.0 },
  { tier: 2, speed: 84, minIntensity: 0.25 },
  { tier: 3, speed: 110, minIntensity: 0.5 },
  { tier: 4, speed: 140, minIntensity: 0.75 },
  { tier: 5, speed: 180, minIntensity: 1.0 },
];
export const SPEED_LERP_TIME = 1.5; // s. Speed changes are lerped, never stepped.

// ── §5 Audio clock ─────────────────────────────────────────────────────────
export const DEFAULT_BPM = 128;

// ── §7 Player & collision constants ────────────────────────────────────────
export const SHIP_VISUAL_RADIUS = 0.6; // u. What you see.
export const SHIP_HITBOX_RADIUS = 0.45; // u. What kills you — deliberately smaller.
export const GRAZE_RADIUS = 1.5; // u. Ring around the hitbox that awards Resonance.
export const MIN_NODE_WIDTH = 2.2; // u. Absolute floor on any safe gap. Never violated.
export const LATERAL_ACCEL = 78; // u/s².
export const MAX_LATERAL_SPEED = 26; // u/s.
export const DAMPING = 6.5; // /s. Exponential: v *= exp(-DAMPING * dt).
export const BANK_MAX = (42 * Math.PI) / 180; // rad. Roll proportional to lateral velocity.
export const IFRAME_DURATION = 1.2; // s.

// ── §7.1 Anti-camping (Amendment PT-1) ─────────────────────────────────────
/** Longest a fixed playfield position may remain non-lethal. FS-17 R32's dwell probe enforces it. */
export const MAX_SAFE_DWELL = 4.0; // s
/** Longest gap between hazards whose lethal region covers the tunnel axis, at Intensity >= 0.25. */
export const AXIS_PRESSURE_PERIOD = 8; // beats
/** The rule is scoped to this intensity and above, so Intro and Breakdown are exempt by construction. */
export const AXIS_PRESSURE_MIN_INTENSITY = 0.25;
/** Minimum spacing between consecutive opposite-polarity hazards. Checked in SECONDS (Bible §7.2). */
export const POLARITY_FLIP_MIN_BEATS = 2;

// ── §8 Resonance & scoring ─────────────────────────────────────────────────
export const RESONANCE_MAX = 100;
export const RES_GRAZE = 6;
export const RES_NODE_CENTRE = 4;
export const RES_BEAT_STEER = 2;
export const RES_IDLE_PER_S = -4;
export const RES_IDLE_AFTER_S = 1.0;

export const OVERDRIVE_DURATION = 10; // s.
export const OVERDRIVE_SCORE_MULT = 3;
export const MULTIPLIER_MAX = 8;
export const MULTIPLIER_STEP_HAZARDS = 8; // one step per 8 consecutive hazards cleared.

export const SCORE_DISTANCE_RATE = 0.1; // Σ (distance_u × 0.1 × intensity).

// ── §11 Performance budget / pool sizes (Medium tier defaults) ──────────────
export const LIVE_HAZARD_CAP = 48;
